import React from 'react';
import { Firestore } from 'firebase/firestore';
import { Trash2, Copy, ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { PatternStep, PatternConfig, PatternRule, PromptOverride } from 'services/apiService';
import StepEditor from './StepEditor';
import type { AdditionalPictureOverride } from './StepEditor';
import PromptOverrideEditor from './PromptOverrideEditor';

interface RuleEditorProps {
  rule: PatternRule;
  index: number;
  selectedColumns: string[];
  isExpanded: boolean;
  patternPreview: PatternStep[] | undefined;
  projectId: string;
  db: Firestore;
  uid: string;
  isLoadingPattern: boolean;
  patternError?: string;
  onToggleExpanded: () => void;
  onUpdateRule: (updates: Partial<PatternRule>) => void;
  onUpdatePatternConfig: (updates: Partial<PatternConfig>) => void;
  onDeleteRule: () => void;
  onDuplicateRule: () => void;
  onLoadPattern: () => void;
  onAddPromptOverride: (stepIndex: number) => void;
  onUpdatePromptOverride: (stepIndex: number, overrideIndex: number, updates: Partial<PromptOverride>) => void;
  onRemovePromptOverride: (stepIndex: number, overrideIndex: number) => void;
  onUpdateAdditionalPictureOverride: (stepIndex: number, picIndex: number, override: AdditionalPictureOverride) => void;
  getAdditionalPictureOverrides: (stepIndex: number) => AdditionalPictureOverride[];
}

const RuleEditor: React.FC<RuleEditorProps> = ({
  rule,
  index,
  selectedColumns,
  isExpanded,
  patternPreview,
  projectId,
  db,
  uid,
  isLoadingPattern,
  patternError,
  onToggleExpanded,
  onUpdateRule,
  onUpdatePatternConfig,
  onDeleteRule,
  onDuplicateRule,
  onLoadPattern,
  onAddPromptOverride,
  onUpdatePromptOverride,
  onRemovePromptOverride,
  onUpdateAdditionalPictureOverride,
  getAdditionalPictureOverrides,
}) => {
  return (
    <div className="bg-gray-900/50 border border-gray-700 rounded-lg overflow-hidden">
      {/* Rule Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-800/50 transition-colors"
        onClick={onToggleExpanded}
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
          <span className="text-sm font-semibold text-gray-300">
            Rule {index + 1}
          </span>
          {rule.condition.value && (
            <span className="text-xs px-2 py-1 bg-purple-500/20 border border-purple-500/30 text-purple-300 rounded">
              {rule.condition.column} {rule.condition.operator} "{rule.condition.value}"
            </span>
          )}
          {patternPreview && (
            <span className="text-xs px-2 py-1 bg-green-500/20 border border-green-500/30 text-green-300 rounded">
              {patternPreview.length} steps loaded
            </span>
          )}
        </div>
        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onDuplicateRule}
            className="p-1.5 text-gray-400 hover:text-blue-400 transition-colors"
            title="Duplicate rule"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={onDeleteRule}
            className="p-1.5 text-gray-400 hover:text-red-400 transition-colors"
            title="Delete rule"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-700 p-4 space-y-4">
          {/* Condition (IF statement) */}
          <div className="bg-black/30 rounded-lg p-3 space-y-2">
            <label className="text-xs text-gray-400 uppercase tracking-wide">
              When (Condition)
            </label>
            <div className="grid grid-cols-3 gap-2">
              <select
                value={rule.condition.column}
                onChange={(e) => onUpdateRule({
                  condition: { ...rule.condition, column: e.target.value }
                })}
                className="bg-gray-800 border border-gray-600 text-gray-200 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
              >
                {selectedColumns.map(col => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>

              <select
                value={rule.condition.operator}
                onChange={(e) => onUpdateRule({
                  condition: { ...rule.condition, operator: e.target.value }
                })}
                className="bg-gray-800 border border-gray-600 text-gray-200 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
              >
                <option value="=">equals (=)</option>
                <option value="!=">not equals (&ne;)</option>
                <option value="contains">contains</option>
                <option value="starts_with">starts with</option>
                <option value="ends_with">ends with</option>
                <option value=">">&gt; greater than (&gt;)</option>
                <option value="<">less than (&lt;)</option>
                <option value=">=">&gt;= greater or equal (&ge;)</option>
                <option value="<=">&le; less or equal (&le;)</option>
              </select>

              <input
                type="text"
                value={rule.condition.value}
                onChange={(e) => onUpdateRule({
                  condition: { ...rule.condition, value: e.target.value }
                })}
                placeholder="value"
                className="bg-gray-800 border border-gray-600 text-gray-200 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Pattern Source Selector */}
          <div className="bg-black/30 rounded-lg p-3">
            <label className="text-xs text-gray-400 uppercase tracking-wide mb-3 block">
              Pattern Source
            </label>
            <PromptOverrideEditor
              projectId={projectId}
              db={db}
              uid={uid}
              patternConfig={rule.pattern_config}
              onPatternConfigChange={onUpdatePatternConfig}
              onLoadPattern={onLoadPattern}
              isLoadingPattern={isLoadingPattern}
              patternError={patternError}
            />
          </div>

          {/* Pattern Steps Preview & Customization */}
          {patternPreview && patternPreview.length > 0 && (
            <div className="bg-black/30 rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs text-gray-400 uppercase tracking-wide flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Pattern Steps ({patternPreview.length})
                </label>
                <p className="text-xs text-gray-500">
                  Customize prompts and reference images for each step
                </p>
              </div>

              <div className="space-y-2">
                {patternPreview.map((step, stepIndex) => (
                  <StepEditor
                    key={step.picture_id}
                    step={step}
                    stepIndex={stepIndex}
                    ruleId={rule.id}
                    selectedColumns={selectedColumns}
                    promptOverrides={rule.pattern_config.prompt_overrides?.[String(stepIndex)] || []}
                    additionalPictureOverrides={getAdditionalPictureOverrides(stepIndex)}
                    onAddPromptOverride={() => onAddPromptOverride(stepIndex)}
                    onUpdatePromptOverride={(idx, updates) => onUpdatePromptOverride(stepIndex, idx, updates)}
                    onRemovePromptOverride={(idx) => onRemovePromptOverride(stepIndex, idx)}
                    onUpdateAdditionalPictureOverride={(picIdx, override) =>
                      onUpdateAdditionalPictureOverride(stepIndex, picIdx, override)
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RuleEditor;
